// StockImagePicker — modal for browsing Pexels and Pixabay stock photos.
//
// When a user clicks an image the component calls api.campaigns.importImageFromUrl()
// to download it into R2, then fires onImported(r2Key) so the caller can update
// campaign state without needing to know about the download mechanics.

import { useEffect, useRef, useState } from "react";
import { api, type StockPhoto } from "../lib/api";

interface Props {
  brandId: string;
  campaignId: string;
  /** Called after the selected image has been successfully downloaded into R2. */
  onImported: (r2Key: string, photo: StockPhoto) => void;
  onClose: () => void;
}

type Source = "pexels" | "pixabay";

const SOURCE_LABELS: Record<Source, string> = {
  pexels: "Pexels",
  pixabay: "Pixabay",
};

const SOURCE_ATTRIBUTION: Record<Source, string> = {
  pexels: "Photos provided by Pexels",
  pixabay: "Photos provided by Pixabay",
};

const PER_PAGE = 20;

export default function StockImagePicker({
  brandId,
  campaignId,
  onImported,
  onClose,
}: Props) {
  const [source, setSource] = useState<Source>("pexels");
  const [query, setQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [page, setPage] = useState(1);

  const [photos, setPhotos] = useState<StockPhoto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [importing, setImporting] = useState<string | null>(null); // photoId being imported
  const [importError, setImportError] = useState<string | null>(null);

  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the search input when the modal opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Re-run search whenever committedQuery, source, or page changes
  useEffect(() => {
    if (!committedQuery) return;

    let cancelled = false;
    setLoading(true);
    setSearchError(null);

    api.stockImages
      .search(source, committedQuery, page, PER_PAGE)
      .then((result) => {
        if (!cancelled) {
          setPhotos(result.photos);
          setTotal(result.total);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSearchError(
            err instanceof Error
              ? err.message
              : "Search failed. Please try again.",
          );
          setPhotos([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [source, committedQuery, page]);

  const handleSearch = () => {
    const q = query.trim();
    if (!q) return;
    setPage(1);
    setCommittedQuery(q);
  };

  const handleTabChange = (newSource: Source) => {
    setSource(newSource);
    setPage(1);
    // Rerun the search on the new source if there's an active query
    if (committedQuery) {
      setPhotos([]);
    }
  };

  const handleSelect = async (photo: StockPhoto) => {
    if (importing) return;
    setImporting(photo.id);
    setImportError(null);
    try {
      const { r2Key } = await api.campaigns.importImageFromUrl(
        brandId,
        campaignId,
        photo.downloadUrl,
      );
      onImported(r2Key, photo);
      onClose();
    } catch (err) {
      setImportError(
        err instanceof Error
          ? err.message
          : "Failed to import image. Please try again.",
      );
    } finally {
      setImporting(null);
    }
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const hasResults = photos.length > 0;
  const hasSearched = !!committedQuery;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal panel */}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            Browse stock photos
          </h2>
          <button
            onClick={onClose}
            aria-label="Close image picker"
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* ── Source tabs ── */}
        <div className="flex gap-0 px-5 pt-3 border-b border-gray-100">
          {(["pexels", "pixabay"] as Source[]).map((s) => (
            <button
              key={s}
              onClick={() => handleTabChange(s)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                source === s
                  ? "border-brand-500 text-brand-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {SOURCE_LABELS[s]}
            </button>
          ))}
        </div>

        {/* ── Search bar ── */}
        <div className="px-5 py-3 flex gap-2">
          <input
            ref={inputRef}
            type="text"
            className="input flex-1 text-sm"
            placeholder={`Search ${SOURCE_LABELS[source]} photos…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            onClick={handleSearch}
            disabled={!query.trim() || loading}
            className="btn-primary text-sm px-4 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />
                Searching…
              </span>
            ) : (
              "Search"
            )}
          </button>
        </div>

        {/* ── Import error ── */}
        {importError && (
          <div className="mx-5 mb-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {importError}
          </div>
        )}

        {/* ── Results area ── */}
        <div className="flex-1 overflow-y-auto px-5 pb-3">
          {/* Loading shimmer */}
          {loading && (
            <div className="grid grid-cols-4 gap-2 mt-1">
              {Array.from({ length: PER_PAGE }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-xl bg-gray-100 animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Search error */}
          {!loading && searchError && (
            <div className="py-10 text-center">
              <p className="text-sm text-red-600">{searchError}</p>
              <button
                onClick={handleSearch}
                className="mt-3 text-xs text-brand-600 underline"
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty state — no search yet */}
          {!loading && !searchError && !hasSearched && (
            <div className="py-12 text-center text-gray-400">
              <p className="text-2xl mb-2">🖼️</p>
              <p className="text-sm">Search for photos to use in your posts.</p>
              <p className="text-xs mt-1 text-gray-300">
                Millions of free, commercially licensed photos from{" "}
                {SOURCE_LABELS[source]}.
              </p>
            </div>
          )}

          {/* Empty state — search returned nothing */}
          {!loading && !searchError && hasSearched && !hasResults && (
            <div className="py-12 text-center text-gray-400">
              <p className="text-sm">
                No photos found for &ldquo;{committedQuery}&rdquo;.
              </p>
              <p className="text-xs mt-1">Try a different search term.</p>
            </div>
          )}

          {/* Photo grid */}
          {!loading && !searchError && hasResults && (
            <div className="grid grid-cols-4 gap-2 mt-1">
              {photos.map((photo) => {
                const isImporting = importing === photo.id;
                const isHovered = hoveredId === photo.id;
                return (
                  <button
                    key={photo.id}
                    onClick={() => handleSelect(photo)}
                    onMouseEnter={() => setHoveredId(photo.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    disabled={!!importing}
                    aria-label={`Select photo by ${photo.photographer}`}
                    className="relative group aspect-square rounded-xl overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 disabled:cursor-wait transition-transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <img
                      src={photo.thumbUrl}
                      alt={photo.alt}
                      className="w-full h-full object-cover block"
                      loading="lazy"
                    />

                    {/* Hover overlay with attribution */}
                    {(isHovered || isImporting) && (
                      <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1 p-2">
                        {isImporting ? (
                          <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <>
                            <span className="text-white text-xs font-semibold">
                              Use this photo
                            </span>
                            <span className="text-white/70 text-[10px] text-center leading-tight truncate w-full">
                              {photo.photographer}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer: pagination + attribution ── */}
        {hasResults && !loading && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3">
            {/* Attribution */}
            <p className="text-[11px] text-gray-400 flex-shrink-0">
              {SOURCE_ATTRIBUTION[source]} &mdash; always credit the
              photographer when publishing.
            </p>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Prev
                </button>
                <span className="text-xs text-gray-500">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
